
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { puttingSituations } from "@/data/puttingSituations";
import PuttingTest from "@/components/PuttingTest";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import PuttingHeader from "@/components/putting/PuttingHeader";
import PuttingTitle from "@/components/putting/PuttingTitle";
import PuttingCardGrid from "@/components/putting/PuttingCardGrid";
import NavigationMenu from "@/components/layout/NavigationMenu";
import PuttingFavorites from "@/components/putting/PuttingFavorites";
import { useFavorites } from "@/hooks/useFavorites";

const Putting = () => {
  const [selectedSituation, setSelectedSituation] = useState<number | null>(null);
  const [user, setUser] = useState<boolean>(false);
  const [username, setUsername] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refreshFavorites } = useFavorites();

  const fetchUserData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }
    setUser(true);
    const { data } = await supabase
      .from("profiles")
      .select("username")
      .eq("id", user.id)
      .single();
    
    if (data) {
      setUsername(data.username);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [navigate]);

  useEffect(() => {
    // Refresh favorites whenever the component mounts or refreshKey changes
    if (user) {
      refreshFavorites();
    }
  }, [refreshKey, user, refreshFavorites]);

  if (!user) {
    return null;
  }

  const handleSelectSituation = (id: number) => {
    setSelectedSituation(id);
    
    // Force refresh of favorites data with a slight delay to ensure DB operation completes
    setTimeout(() => {
      refreshFavorites();
      setRefreshKey(prevKey => prevKey + 1);
    }, 300);
  };

  const currentSituation = selectedSituation !== null 
    ? {
        ...puttingSituations.find(s => s.id === selectedSituation),
        scoring: puttingSituations.find(s => s.id === selectedSituation)?.scoring.map(score => ({
          ...score,
          condition: score.condition
            .replace("(1pts)", "(1pt)")
            .replace("(0pts)", "(0pt)")
        }))
      }
    : null;

  // Add a function to refresh favorites that can be used after toast notifications
  const refreshAfterToast = () => {
    setTimeout(() => {
      refreshFavorites();
      setRefreshKey(prevKey => prevKey + 1);
    }, 300);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      
      <div className="container mx-auto py-8">
        <PuttingHeader username={username} />
        
        <PuttingTitle />

        {!currentSituation && (
          <div className="mb-8">
            <PuttingFavorites 
              onSelectSituation={handleSelectSituation}
              refreshAfterToast={refreshAfterToast}
              key={`favorites-${refreshKey}`} // Use key with refreshKey to force re-render
            />
          </div>
        )}

        {currentSituation ? (
          <PuttingTest 
            situation={currentSituation} 
            onClose={() => {
              setSelectedSituation(null);
              // Refresh favorites when closing a test
              setTimeout(() => {
                refreshFavorites();
                setRefreshKey(prevKey => prevKey + 1);
              }, 300);
            }}
            refreshAfterToast={refreshAfterToast}
          />
        ) : (
          <PuttingCardGrid
            situations={puttingSituations}
            onSelectSituation={handleSelectSituation}
            refreshAfterToast={refreshAfterToast}
          />
        )}
      </div>
    </div>
  );
};

export default Putting;
